// Player Bio widget (Players canvas) — consumes the picked player (playerId).
// Replaces the Slice C canary: the player now comes from the filter bar, not an
// in-widget dropdown. Data from GET /player.

import type { PlayerBio } from "@catbento/shared";
import { usePlayerBio } from "../data/players.js";
import type { WidgetScope } from "./scope.js";
import { theme } from "../lib/theme.js";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? parts[parts.length - 1]![0] : "")).toUpperCase();
}

function formatDob(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d} ${months[m - 1]} ${y}`;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: theme.field, borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ color: theme.inkMuted, fontSize: 12, marginBottom: 3 }}>{label}</div>
      <div style={{ color: theme.ink, fontSize: 14 }}>{value}</div>
    </div>
  );
}

function Card({ player }: { player: PlayerBio }) {
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            background: theme.accentSurface,
            color: theme.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          {initials(player.name)}
        </div>
        <div>
          <div style={{ color: theme.ink, fontSize: 19, fontWeight: 500 }}>{player.name}</div>
          {player.nickname && <div style={{ color: theme.inkMuted, fontSize: 13 }}>{player.nickname}</div>}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
        <Field label="Country" value={player.country ?? "—"} />
        <Field label="Date of birth" value={formatDob(player.dob)} />
        <Field label="Height" value={player.height ? `${player.height} cm` : "—"} />
        <Field label="Weight" value={player.weight ? `${Math.round(player.weight)} kg` : "—"} />
        <Field label="Most recent team" value={player.mostRecentTeam ?? "—"} />
        <Field label="Most recent match" value={formatDob(player.mostRecentMatchDate)} />
      </div>
    </>
  );
}

export default function BioWidget({ scope }: { scope: WidgetScope }) {
  const { data, isPending, isError, error } = usePlayerBio(scope.playerId);
  if (scope.playerId === null)
    return <div style={{ color: theme.inkMuted, fontSize: 13 }}>Search for a player above.</div>;
  if (isPending) return <div style={{ color: theme.inkMuted, fontSize: 13 }}>Loading bio…</div>;
  if (isError) return <div style={{ color: theme.danger, fontSize: 13 }}>Couldn't load bio. {String(error)}</div>;
  return <Card player={data.player} />;
}
